import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Check, Upload, X, File, FileText, Image as ImageIcon, CheckCircle } from '@phosphor-icons/react';
import { formatApiErrorDetail } from '../utils/helpers';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const slideVariant = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, x: -40, transition: { duration: 0.25 } },
};

const QuotePage = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(searchParams.get('product') || '');
  const [productConfigs, setProductConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [options, setOptions] = useState({});
  const [quantity, setQuantity] = useState(100);
  const [files, setFiles] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({ name: '', phone: '', email: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const startingPrices = {
  "Business Cards": "€30",
  "Flyers": "€30",
  "Brochures": "€70",
  "Bookmarks": "€25",
  "Envelopes": "€40",
  "Invitations (Wedding / Party)": "€60",
  "Document Printing": "€8",
  "Notepads (Business)": "€50",
  "Pads (General small print)": "€35",
  "Custom Request": "€30"
};

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/product-configs?active_only=true`)
      .then(({ data }) => setProductConfigs(data.sort((a, b) => (a.order || 99) - (b.order || 99))))
      .catch(() => setError('Failed to load products. Please try again.'));
  }, []);

const slugify = (str) =>
  str
    .toLowerCase()
    .replace(/[()\/]/g, '')   // remove parentheses and slashes
    .replace(/[^\w\s-]/g, '') // remove special characters
    .replace(/\s+/g, '-');

useEffect(() => {
  if (selectedProduct && step === 1 && productConfigs.length > 0) {

    const config = productConfigs.find(
      p => slugify(p.product) === selectedProduct
    );

    if (config) {
      setSelectedProduct(config.product);
      setSelectedConfig(config);
      setStep(2);
    }
  }
}, [selectedProduct, productConfigs, step]);
  
  const onDrop = useCallback((acceptedFiles) => setFiles(prev => [...prev, ...acceptedFiles]), []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif'], 'application/pdf': ['.pdf'], 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'], 'application/msword': ['.doc'] },
  });

  const removeFile = (index) => setFiles(files.filter((_, i) => i !== index));

const handleSubmit = async () => {
  setLoading(true);
  setError('');
  try {
    const formData = new FormData();
    formData.append('product', selectedProduct);
    formData.append('quantity', quantity.toString());
    formData.append('customer_name', customerInfo.name);
    formData.append('customer_phone', customerInfo.phone);
    formData.append('customer_email', customerInfo.email);
    formData.append('notes', customerInfo.notes);
    formData.append('options', JSON.stringify(options));
    files.forEach((file) => formData.append('files', file));

    await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/quotes`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    // âœ… Î‘Î¥Î¤ÎŸ ÎœÎ Î‘Î™ÎÎ•Î™
    navigate('/quote-success');

  } catch (err) {
    setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to submit quote. Please try again.');
  } finally {
    setLoading(false);
  }
};

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return <ImageIcon size={22} weight="fill" className="text-blue-500" />;
    if (file.type === 'application/pdf') return <FileText size={22} weight="fill" className="text-red-500" />;
    return <File size={22} weight="fill" className="text-[#4A3F35]" />;
  };

  const stepLabels = ['Product', 'Options', 'Files', 'Contact'];

  // Helper to get option name (supports both string and object format)
  const getOptName = (opt) => typeof opt === 'string' ? opt : opt.name;
  const getOptPopular = (opt) => typeof opt === 'object' && opt.isPopular;

  

  return (
    <div className="paper-texture-bg min-h-screen">
      {/* Header */}
      <div className="relative z-10 bg-[#F5F3ED]/80 backdrop-blur-md border-b border-[#1a1410]/[0.06] px-6 md:px-12 py-5">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-[#4A3F35] hover:text-[#1a1410] mb-4 transition-colors text-sm">
            <ArrowLeft size={18} />Back to Home
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1a1410] mb-5" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="quote-page-heading">
            Request a Quote
          </h1>

          {/* Step indicator */}
          <div className="flex items-center gap-2" data-testid="quote-progress-bar">
            {stepLabels.map((label, i) => {
              const stepNum = i + 1;
              const isActive = step === stepNum;
              const isComplete = step > stepNum;
              return (
                <React.Fragment key={label}>
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${isComplete ? 'bg-[#C6A87A] text-[#1a1410]' : isActive ? 'bg-[#1a1410] text-[#F5F3ED]' : 'bg-[#1a1410]/10 text-[#4A3F35]/50'}`}>
                      {isComplete ? <Check size={14} weight="bold" /> : stepNum}
                    </div>
                    <span className={`text-xs font-medium hidden sm:inline ${isActive ? 'text-[#1a1410]' : 'text-[#4A3F35]/40'}`}>{label}</span>
                  </div>
                  {i < stepLabels.length - 1 && <div className={`flex-1 h-px max-w-[60px] transition-colors duration-300 ${step > stepNum ? 'bg-[#C6A87A]' : 'bg-[#1a1410]/10'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {error && (
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 mt-6">
          <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-3 rounded-lg text-sm" data-testid="quote-error-message">
            {error}
            <button onClick={() => setError('')} className="ml-3 underline text-xs">Dismiss</button>
          </div>
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="grid lg:grid-cols-[1fr_380px] gap-8 items-start">
          {/* LEFT SIDE - FORM */}
          <div>
            <AnimatePresence mode="wait">
              {/* â”€â”€â”€ STEP 1: Product Selection â”€â”€â”€ */}
              {step === 1 && (
                <motion.div key="s1" variants={slideVariant} initial="initial" animate="animate" exit="exit" data-testid="quote-step-product-selection">
                  <div className="bg-[#FDFDFC] rounded-xl p-7 md:p-9 shadow-[0_2px_24px_rgba(26,20,16,0.06)] border border-[#1a1410]/[0.04]">
                    <h2 className="text-xl md:text-2xl font-bold mb-2 text-[#1a1410]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      What would you like to print?
                    </h2>
                    <p className="text-sm text-[#4A3F35]/60 mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>Choose a product category to start your quote</p>
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {productConfigs.map((config) => (
                        <button
                          key={config.id}
                          onClick={() => { setSelectedProduct(config.product); setSelectedConfig(config); setStep(2); }}
                          data-testid={`select-product-${config.product.toLowerCase().replace(/\s+/g, '-')}`}
                          className="group relative p-5 bg-[#F5F3ED] border border-[#1a1410]/[0.06] hover:border-[#C6A87A]/50 hover:shadow-lg transition-all duration-300 text-left rounded-xl hover:-translate-y-1"
                        >
                          <h3 className="font-semibold text-sm text-[#1a1410] group-hover:text-[#C6A87A] transition-colors mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                            {config.product}
                          </h3>
                          <p className="text-xs text-[#4A3F35]/40">{config.fields.length} options</p>
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* â”€â”€â”€ STEP 2: Product Options â”€â”€â”€ */}
              {step === 2 && selectedConfig && (
                <motion.div key="s2" variants={slideVariant} initial="initial" animate="animate" exit="exit" data-testid="quote-step-product-options">
                  <div className="bg-[#FDFDFC] rounded-xl p-7 md:p-9 shadow-[0_2px_24px_rgba(26,20,16,0.06)] border border-[#1a1410]/[0.04] mb-6">
                    <h2 className="text-xl md:text-2xl font-bold mb-1 text-[#1a1410]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Configure Your {selectedProduct}
                    </h2>
                    <motion.div
  initial={{ opacity: 0, y: 10 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.4 }}
  className="mb-6 p-5 rounded-xl border border-[#C6A87A]/30 bg-gradient-to-br from-[#C6A87A]/10 to-[#F5F3ED]"
>
  <p className="text-xs uppercase tracking-widest text-[#4A3F35]/50 mb-1">
    Starting Price
  </p>

  <p className="text-2xl font-bold text-[#1a1410]">
    From {startingPrices[selectedProduct] || "€30"}
  </p>

  <p className="text-xs text-[#4A3F35]/50 mt-1">
    Final price depends on quantity & specifications
  </p>
</motion.div>
                    <p className="text-sm text-[#4A3F35]/60 mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>Choose your specifications below</p>

                    <div className="space-y-8">
                      {selectedConfig.fields.map((field, index) => (
                        <div key={index}>
                          <Label className="mb-3 block text-sm font-semibold text-[#1a1410]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {field.name} {field.required && <span className="text-[#C6A87A]">*</span>}
                          </Label>

                          {field.type === 'select' && field.options ? (
                            <div className="flex flex-wrap gap-3">
                              {field.options.map((opt) => {
                                const optName = getOptName(opt);
                                const isSelected = options[field.name] === optName;
                                const isPopular = getOptPopular(opt);
                                return (
                                  <button
                                    key={optName}
                                    onClick={() => setOptions({ ...options, [field.name]: optName })}
                                    className={`option-card ${isSelected ? 'selected' : ''}`}
                                    data-testid={`option-${field.name}-${optName.toLowerCase().replace(/\s+/g, '-')}`}
                                  >
                                    {isPopular && <span className="popular-badge">Popular</span>}
                                    <span className="text-sm">{optName}</span>
                                    {isSelected && <CheckCircle size={16} weight="fill" className="inline ml-2 text-[#C6A87A]" />}
                                  </button>
                                );
                              })}
                            </div>
                          ) : field.type === 'textarea' ? (
                            <Textarea
                              rows={4}
                              placeholder={field.placeholder || `Enter ${field.name}`}
                              className="bg-[#F5F3ED] border-[#1a1410]/[0.08] focus:border-[#C6A87A] rounded-xl"
                              onChange={(e) => setOptions({ ...options, [field.name]: e.target.value })}
                              data-testid={`option-${field.name}`}
                            />
                          ) : (
                            <Input
                              type={field.type === 'number' ? 'number' : 'text'}
                              placeholder={field.placeholder || `Enter ${field.name}`}
                              className="bg-[#F5F3ED] border-[#1a1410]/[0.08] focus:border-[#C6A87A] rounded-xl max-w-sm"
                              onChange={(e) => setOptions({ ...options, [field.name]: e.target.value })}
                              data-testid={`option-${field.name}`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Quantity Card */}
                  <div className="bg-[#FDFDFC] rounded-xl p-7 md:p-9 shadow-[0_2px_24px_rgba(26,20,16,0.06)] border border-[#1a1410]/[0.04]">
                    <Label className="mb-3 block text-sm font-semibold text-[#1a1410]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Quantity <span className="text-[#C6A87A]">*</span>
                    </Label>
                    <p className="text-xs text-[#4A3F35]/40 mb-3">How many copies do you need?</p>
                    <Input
                      type="number" value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value) || 1)} min="1"
                      className="bg-[#F5F3ED] border-[#1a1410]/[0.08] focus:border-[#C6A87A] rounded-xl max-w-[180px] text-lg font-semibold"
                      data-testid="quote-quantity-input"
                    />
                  </div>

                  <div className="flex gap-4 mt-8">
                    <Button variant="outline" onClick={() => { setStep(1); setSelectedProduct(''); }} className="border-[#1a1410]/10 text-[#4A3F35] hover:bg-[#1a1410]/5 rounded-lg" data-testid="quote-back-button">
                      <ArrowLeft size={18} className="mr-2" />Back
                    </Button>
                    <Button onClick={() => setStep(3)} className="flex-1 bg-[#C6A87A] hover:bg-[#B39567] text-[#1a1410] font-semibold rounded-lg py-5" data-testid="quote-next-button-options">
                      Continue<ArrowRight size={18} className="ml-2" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* â”€â”€â”€ STEP 3: File Upload â”€â”€â”€ */}
              {step === 3 && (
                <motion.div key="s3" variants={slideVariant} initial="initial" animate="animate" exit="exit" data-testid="quote-step-file-upload">
                  <div className="bg-[#FDFDFC] rounded-xl p-7 md:p-9 shadow-[0_2px_24px_rgba(26,20,16,0.06)] border border-[#1a1410]/[0.04]">
                    <h2 className="text-xl md:text-2xl font-bold mb-1 text-[#1a1410]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Upload Your Files
                    </h2>
                    <p className="text-sm text-[#4A3F35]/60 mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>Attach your design files (optional)</p>

                    <div
                      {...getRootProps()}
                      className={`border-2 border-dashed rounded-xl py-14 text-center cursor-pointer transition-all duration-300 ${isDragActive ? 'border-[#C6A87A] bg-[#C6A87A]/5' : 'border-[#1a1410]/10 hover:border-[#C6A87A]/50 hover:bg-[#C6A87A]/[0.02]'}`}
                      data-testid="file-upload-dropzone"
                    >
                      <input {...getInputProps()} />
                      <Upload size={48} className="mx-auto mb-4 text-[#C6A87A]/60" />
                      <p className="text-sm font-medium text-[#1a1410] mb-1">Drag & drop files here</p>
                      <p className="text-xs text-[#4A3F35]/40">or click to browse â€” PNG, JPG, PDF, DOC</p>
                    </div>

                    {files.length > 0 && (
                      <div className="mt-6 space-y-2.5">
                        {files.map((file, index) => (
                          <div key={index} className="flex items-center gap-3 p-3.5 bg-[#F5F3ED] rounded-lg border border-[#1a1410]/[0.04]" data-testid={`uploaded-file-${index}`}>
                            {getFileIcon(file)}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-[#1a1410] truncate font-medium">{file.name}</p>
                              <p className="text-xs text-[#4A3F35]/40">{(file.size / 1024).toFixed(1)} KB</p>
                            </div>
                            <button onClick={() => removeFile(index)} className="text-[#4A3F35]/40 hover:text-red-500 transition-colors" data-testid={`remove-file-${index}`}>
                              <X size={18} weight="bold" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-4 mt-8">
                    <Button variant="outline" onClick={() => setStep(2)} className="border-[#1a1410]/10 text-[#4A3F35] hover:bg-[#1a1410]/5 rounded-lg" data-testid="quote-back-button-files">
                      <ArrowLeft size={18} className="mr-2" />Back
                    </Button>
                    <Button onClick={() => setStep(4)} className="flex-1 bg-[#C6A87A] hover:bg-[#B39567] text-[#1a1410] font-semibold rounded-lg py-5" data-testid="quote-next-button-files">
                      Continue<ArrowRight size={18} className="ml-2" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* â”€â”€â”€ STEP 4: Contact â”€â”€â”€ */}
              {step === 4 && (
                <motion.div key="s4" variants={slideVariant} initial="initial" animate="animate" exit="exit" data-testid="quote-step-contact-info">
                  <div className="bg-[#FDFDFC] rounded-xl p-7 md:p-9 shadow-[0_2px_24px_rgba(26,20,16,0.06)] border border-[#1a1410]/[0.04]">
                    <h2 className="text-xl md:text-2xl font-bold mb-1 text-[#1a1410]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Contact Information
                    </h2>
                    <p className="text-sm text-[#4A3F35]/60 mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>How can we reach you with your quote?</p>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label className="mb-2 block text-sm font-semibold text-[#1a1410]">Full Name <span className="text-[#C6A87A]">*</span></Label>
                        <Input value={customerInfo.name} onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })} className="bg-[#F5F3ED] border-[#1a1410]/[0.08] focus:border-[#C6A87A] rounded-xl" data-testid="quote-customer-name" />
                      </div>
                      <div>
                        <Label className="mb-2 block text-sm font-semibold text-[#1a1410]">Phone Number <span className="text-[#C6A87A]">*</span></Label>
                        <Input type="tel" value={customerInfo.phone} onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })} className="bg-[#F5F3ED] border-[#1a1410]/[0.08] focus:border-[#C6A87A] rounded-xl" data-testid="quote-customer-phone" />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="mb-2 block text-sm font-semibold text-[#1a1410]">Email Address <span className="text-[#C6A87A]">*</span></Label>
                        <Input type="email" value={customerInfo.email} onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })} className="bg-[#F5F3ED] border-[#1a1410]/[0.08] focus:border-[#C6A87A] rounded-xl" data-testid="quote-customer-email" />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="mb-2 block text-sm font-semibold text-[#1a1410]">Additional Notes</Label>
                        <p className="text-xs text-[#4A3F35]/40 mb-2">Special requirements, deadlines, or questions</p>
                        <Textarea value={customerInfo.notes} onChange={(e) => setCustomerInfo({ ...customerInfo, notes: e.target.value })} rows={4} className="bg-[#F5F3ED] border-[#1a1410]/[0.08] focus:border-[#C6A87A] rounded-xl" data-testid="quote-customer-notes" />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4 mt-8">
                    <Button variant="outline" onClick={() => setStep(3)} className="border-[#1a1410]/10 text-[#4A3F35] hover:bg-[#1a1410]/5 rounded-lg" data-testid="quote-back-button-contact">
                      <ArrowLeft size={18} className="mr-2" />Back
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      className="flex-1 bg-[#C6A87A] hover:bg-[#B39567] text-[#1a1410] font-semibold rounded-lg py-5"
                      disabled={loading || !customerInfo.name || !customerInfo.phone || !customerInfo.email}
                      data-testid="quote-submit-button"
                    >
                      {loading ? 'Submitting...' : 'Submit Quote Request'}
                      <Check size={18} className="ml-2" />
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* RIGHT SIDE - LIVE SUMMARY */}
{step > 1 && step < 5 && (
  <motion.div 
    initial={{ opacity: 0, y: 20 }} 
    animate={{ opacity: 1, y: 0 }} 
    transition={{ duration: 0.5, delay: 0.15 }} 
    className="lg:sticky lg:top-8 h-fit"
  >
    <div className="bg-[#FDFDFC] rounded-xl p-7 shadow-[0_4px_30px_rgba(26,20,16,0.08)] border border-[#1a1410]/[0.04]" data-testid="quote-summary-panel">
      
      <h3 className="text-lg font-bold mb-6 text-[#1a1410]" style={{ fontFamily: 'Playfair Display, serif' }}>
        Order Summary
      </h3>

      {selectedProduct && (
        <>
          {/* PRODUCT */}
          <div className="mb-5 pb-5 border-b border-[#1a1410]/[0.06]">
            <p className="text-[10px] text-[#4A3F35]/40 mb-1 uppercase tracking-widest font-semibold">
              Product
            </p>
            <p className="text-base font-semibold text-[#1a1410]">
              {selectedProduct}
            </p>
          </div>

          {/* ?? STARTING PRICE (NEW) */}
          <div className="mb-5 pb-5 border-b border-[#1a1410]/[0.06]">
            <p className="text-[10px] text-[#4A3F35]/40 mb-1 uppercase tracking-widest font-semibold">
              Starting Price
            </p>

            <motion.p 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="text-2xl font-bold text-[#C6A87A]"
            >
              {startingPrices[selectedProduct] || "€30"}+
            </motion.p>
          </div>
        </>
      )}

      {/* OPTIONS */}
      {Object.keys(options).length > 0 && (
        <div className="mb-5 pb-5 border-b border-[#1a1410]/[0.06]">
          <p className="text-[10px] text-[#4A3F35]/40 mb-3 uppercase tracking-widest font-semibold">
            Specifications
          </p>
          <div className="space-y-2">
            {Object.entries(options).filter(([, v]) => v).map(([key, value]) => (
              <div key={key} className="flex justify-between text-sm">
                <span className="text-[#4A3F35]/60">{key}</span>
                <span className="text-[#1a1410] font-medium text-right max-w-[160px] truncate">
                  {value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
                <div className="mb-5 pb-5 border-b border-[#1a1410]/[0.06]">
                  <p className="text-[10px] text-[#4A3F35]/40 mb-1 uppercase tracking-widest font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Quantity</p>
                  <p className="text-2xl font-bold text-[#C6A87A]">{quantity}</p>
                </div>

                {files.length > 0 && (
                  <div className="mb-5 pb-5 border-b border-[#1a1410]/[0.06]">
                    <p className="text-[10px] text-[#4A3F35]/40 mb-2 uppercase tracking-widest font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Files ({files.length})</p>
                    <div className="space-y-1.5">
                      {files.map((file, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          {getFileIcon(file)}
                          <span className="text-[#1a1410] truncate flex-1">{file.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {step === 4 && customerInfo.name && (
                  <div>
                    <p className="text-[10px] text-[#4A3F35]/40 mb-2 uppercase tracking-widest font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Contact</p>
                    <p className="text-sm font-medium text-[#1a1410]">{customerInfo.name}</p>
                    {customerInfo.email && <p className="text-xs text-[#4A3F35]/50">{customerInfo.email}</p>}
                    {customerInfo.phone && <p className="text-xs text-[#4A3F35]/50">{customerInfo.phone}</p>}
                  </div>
                )}

                {/* Quick help */}
                <div className="mt-6 pt-5 border-t border-[#1a1410]/[0.06]">
                  <p className="text-xs text-[#4A3F35]/40 leading-relaxed">
                    Need help? Call us at <a href="tel:25366249" className="text-[#C6A87A] font-medium hover:underline">25366249</a>
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuotePage;
